import { useState, useCallback, useEffect, useRef } from 'react'
import { parseIntervalToDays } from '~/common/utils/date-utils'

const WEEK_CONTAINER_WIDTH = 32
const PIXELS_PER_DAY = WEEK_CONTAINER_WIDTH / 7  // ~4.57px

// Minimum durations for each phase type (in days)
const MIN_DURATIONS = {
  germination: 3,
  seedling: 7,
  maturing: 7,
  default: 1
}

/**
 * Custom hook for managing phase boundary drag interactions
 *
 * Handle types:
 * - 'start': Dragging the start of the first phase (shifts all phases)
 * - 'end': Dragging the end of the last phase (extends/shrinks last phase only)
 * - number: Intersection between phases (adjusts adjacent phases, cascades to later phases)
 *
 * @param {Object} options
 * @param {Array} options.phases - Array of phase objects from schedule data
 * @param {Function} options.onPhaseAdjust - Callback with updated phases array
 * @returns {Object} Drag state and handlers
 */
export const usePhaseDrag = ({ phases, onPhaseAdjust }) => {
  const [dragState, setDragState] = useState({
    isDragging: false,
    intersectionIndex: null,
    startX: 0,
    startPhases: null,
  })

  const [hoveredIntersection, setHoveredIntersection] = useState(null)

  // Store callbacks in refs to avoid stale closures
  const phasesRef = useRef(phases)
  const onPhaseAdjustRef = useRef(onPhaseAdjust)
  const dragStateRef = useRef(dragState)

  useEffect(() => {
    phasesRef.current = phases
    onPhaseAdjustRef.current = onPhaseAdjust
    dragStateRef.current = dragState
  }, [phases, onPhaseAdjust, dragState])

  const handleDragStart = useCallback((intersectionIndex, clientX) => {
    const currentPhases = phasesRef.current
    if (!currentPhases || currentPhases.length === 0) return

    setDragState({
      isDragging: true,
      intersectionIndex,
      startX: clientX,
      startPhases: currentPhases.map(p => ({
        ...p,
        _startDuration: parseIntervalToDays(p.est_duration)
      })),
    })
  }, [])

  const handleDragMove = useCallback((e) => {
    const state = dragStateRef.current
    if (!state.isDragging || state.startPhases === null) return

    const deltaX = e.clientX - state.startX
    const deltaDays = Math.round(deltaX / PIXELS_PER_DAY)

    if (deltaDays === 0) return

    const { intersectionIndex, startPhases } = state

    let updatedPhases

    if (intersectionIndex === 'start') {
      // Dragging the start of the timeline - shift ALL phases
      updatedPhases = handleStartDrag(startPhases, deltaDays)
    } else if (intersectionIndex === 'end') {
      // Dragging the end of the timeline - only affects last phase duration
      updatedPhases = handleEndDrag(startPhases, deltaDays)
    } else {
      // Dragging an intersection between phases
      updatedPhases = handleIntersectionDrag(startPhases, intersectionIndex, deltaDays)
    }

    if (!updatedPhases) return

    // Remove internal _startDuration property before callback
    const cleanedPhases = updatedPhases.map(({ _startDuration, ...phase }) => phase)

    if (onPhaseAdjustRef.current) {
      onPhaseAdjustRef.current(cleanedPhases)
    }
  }, [])

  const handleDragEnd = useCallback(() => {
    setDragState({
      isDragging: false,
      intersectionIndex: null,
      startX: 0,
      startPhases: null,
    })
  }, [])

  // Attach/detach global mouse listeners
  useEffect(() => {
    if (dragState.isDragging) {
      document.addEventListener('mousemove', handleDragMove)
      document.addEventListener('mouseup', handleDragEnd)

      return () => {
        document.removeEventListener('mousemove', handleDragMove)
        document.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [dragState.isDragging, handleDragMove, handleDragEnd])

  return {
    isDragging: dragState.isDragging,
    draggingIndex: dragState.intersectionIndex,
    hoveredIntersection,
    setHoveredIntersection,
    handleDragStart,
  }
}

/**
 * Handle dragging the start of the timeline
 * Moves the first phase's start date while keeping its END date fixed
 * This means: dragging left = earlier start = longer first phase
 *             dragging right = later start = shorter first phase
 * Subsequent phases are unaffected (their start dates don't change)
 */
function handleStartDrag(startPhases, deltaDays) {
  const firstPhase = startPhases[0]
  if (!firstPhase) return null

  const firstMin = MIN_DURATIONS[firstPhase.phase_type_code] || MIN_DURATIONS.default

  // Moving start left (negative delta) = grow first phase
  // Moving start right (positive delta) = shrink first phase
  const newFirstDuration = firstPhase._startDuration - deltaDays

  // Enforce minimum duration
  if (newFirstDuration < firstMin) {
    return null // Can't shrink below minimum
  }

  // Calculate new start date (end date stays fixed, start moves)
  const originalStart = new Date(firstPhase.est_started_at)
  const originalEnd = new Date(originalStart)
  originalEnd.setDate(originalEnd.getDate() + firstPhase._startDuration)

  // New start = original end - new duration
  const newStartDate = new Date(originalEnd)
  newStartDate.setDate(newStartDate.getDate() - newFirstDuration)

  return startPhases.map((phase, idx) => {
    // First phase: update start date and duration, end stays the same
    if (idx === 0) {
      return {
        ...phase,
        est_started_at: newStartDate.toISOString(),
        est_duration: `${newFirstDuration} days`,
      }
    }

    // All subsequent phases: unchanged (their timing doesn't depend on first phase's start)
    return phase
  })
}

/**
 * Handle dragging the end of the timeline
 * Only affects the last phase's duration
 */
function handleEndDrag(startPhases, deltaDays) {
  const lastIdx = startPhases.length - 1
  const lastPhase = startPhases[lastIdx]
  const lastMin = MIN_DURATIONS[lastPhase.phase_type_code] || MIN_DURATIONS.default

  const newDuration = lastPhase._startDuration + deltaDays

  // Enforce minimum duration
  if (newDuration < lastMin) {
    return null // Can't shrink below minimum
  }

  return startPhases.map((phase, idx) => {
    if (idx === lastIdx) {
      return {
        ...phase,
        est_duration: `${newDuration} days`,
      }
    }
    return phase
  })
}

/**
 * Handle dragging an intersection between two phases
 * Adjusts adjacent phases and cascades start dates to all subsequent phases
 */
function handleIntersectionDrag(startPhases, intersectionIndex, deltaDays) {
  const leftPhase = startPhases[intersectionIndex]
  const rightPhase = startPhases[intersectionIndex + 1]

  if (!leftPhase || !rightPhase) return null

  // Calculate new durations
  const leftOriginalDuration = leftPhase._startDuration
  const rightOriginalDuration = rightPhase._startDuration

  let newLeftDuration = leftOriginalDuration + deltaDays
  let newRightDuration = rightOriginalDuration - deltaDays

  // Get minimum durations
  const leftMin = MIN_DURATIONS[leftPhase.phase_type_code] || MIN_DURATIONS.default
  const rightMin = MIN_DURATIONS[rightPhase.phase_type_code] || MIN_DURATIONS.default

  // Apply constraints
  if (newLeftDuration < leftMin) {
    const adjustment = leftMin - newLeftDuration
    newLeftDuration = leftMin
    newRightDuration += adjustment
  }

  if (newRightDuration < rightMin) {
    const adjustment = rightMin - newRightDuration
    newRightDuration = rightMin
    newLeftDuration -= adjustment
  }

  // Final validation - ensure both meet minimums
  if (newLeftDuration < leftMin || newRightDuration < rightMin) {
    return null // Can't satisfy constraints
  }

  // Calculate the new end date of the right phase
  const leftStart = new Date(startPhases[intersectionIndex].est_started_at)
  const rightStartDate = new Date(leftStart)
  rightStartDate.setDate(rightStartDate.getDate() + newLeftDuration)
  const rightEndDate = new Date(rightStartDate)
  rightEndDate.setDate(rightEndDate.getDate() + newRightDuration)

  // Build updated phases array
  return startPhases.map((phase, idx) => {
    // Phases before intersection: unchanged
    if (idx < intersectionIndex) {
      return phase
    }

    // Left phase at intersection: update duration only
    if (idx === intersectionIndex) {
      return {
        ...phase,
        est_duration: `${newLeftDuration} days`,
      }
    }

    // Right phase at intersection: update start date and duration
    if (idx === intersectionIndex + 1) {
      return {
        ...phase,
        est_started_at: rightStartDate.toISOString(),
        est_duration: `${newRightDuration} days`,
      }
    }

    // Phases after the intersection: shift start dates, keep original duration
    // Calculate cumulative start from the right phase's end
    let cumulativeStart = rightEndDate
    for (let i = intersectionIndex + 2; i < idx; i++) {
      const prevDuration = startPhases[i]._startDuration
      cumulativeStart = new Date(cumulativeStart)
      cumulativeStart.setDate(cumulativeStart.getDate() + prevDuration)
    }

    return {
      ...phase,
      est_started_at: cumulativeStart.toISOString(),
      // Duration stays the same (original _startDuration)
    }
  })
}
